import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Check, 
  FileText, 
  Clock, 
  AlertTriangle,
  Loader2,
  Building2,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ViewProposal = () => {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProposal();
  }, [token]);

  const fetchProposal = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/proposals/view/${token}`);
      if (!res.ok) throw new Error('Proposal not found');
      
      const data = await res.json();
      setProposal(data.proposal);
      setContractor(data.contractor);
      setExpired(data.expired);
      if (data.proposal?.status === 'signed') {
        setSigned(true);
      }
    } catch (err) {
      setError('Proposal not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim()) {
      alert('Please type your full name to sign');
      return;
    }

    setSigning(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/proposals/view/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_name: signatureName })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to sign');
      }
      
      setSigned(true);
      setProposal(prev => ({ 
        ...prev, 
        status: 'signed', 
        signature_name: signatureName,
        signed_at: new Date().toISOString()
      }));
    } catch (err) {
      alert(err.message || 'Failed to sign proposal');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800">{error}</h1>
          <p className="text-gray-600 mt-2">This proposal may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <Clock className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Proposal Expired</h1>
          <p className="text-gray-600">This proposal has expired. Please contact the contractor for a new proposal.</p>
          {contractor && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="font-medium">{contractor.company_name}</p>
              {contractor.phone && <p className="text-sm text-gray-600">{contractor.phone}</p>}
              {contractor.email && <p className="text-sm text-gray-600">{contractor.email}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="view-proposal-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-lg p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Building2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="font-bold text-gray-800">{contractor?.company_name || 'Your Contractor'}</h1>
                <p className="text-sm text-gray-500">Proposal</p>
              </div>
            </div>
            {signed && (
              <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                <Check className="h-4 w-4" />
                <span className="text-sm font-medium">Signed</span>
              </div>
            )}
          </div>
        </div>

        {/* Proposal Content */}
        <div className="bg-white shadow-lg p-6">
          {/* Project Info */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{proposal?.project_name}</h2>
            {proposal?.project_address && (
              <p className="text-gray-600 mt-1 flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {proposal.project_address}
              </p>
            )}
          </div>

          {/* Client Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-500">Prepared for</p>
            <p className="font-medium text-gray-800">{proposal?.client_name}</p>
          </div>

          {/* Description */}
          {proposal?.description && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-700 mb-2">Description</h3>
              <p className="text-gray-600">{proposal.description}</p>
            </div>
          )}

          {/* Line Items */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-3">Scope of Work</h3>
            <div className="border rounded-lg overflow-hidden">
              {proposal?.line_items?.map((item, idx) => (
                <div key={idx} className="flex justify-between p-4 border-b last:border-b-0 bg-white">
                  <span className="text-gray-800">{item.description}</span>
                  <span className="font-medium font-mono">${item.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="bg-emerald-600 text-white rounded-lg p-6 text-center">
            <p className="text-emerald-100 text-sm">TOTAL INVESTMENT</p>
            <p className="text-4xl font-bold mt-1 font-mono">${(proposal?.total_amount || 0).toLocaleString()}</p>
          </div>

          {/* Expiration */}
          {proposal?.expires_at && !signed && (
            <p className="text-center text-sm text-gray-500 mt-4">
              This proposal expires on {new Date(proposal.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Signature Section */}
        <div className="bg-white rounded-b-xl shadow-lg p-6 border-t">
          {signed ? (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full mb-4">
                <Check className="h-5 w-5" />
                <span className="font-medium">Proposal Accepted</span>
              </div>
              <p className="text-gray-600">
                Signed by <strong>{proposal?.signature_name}</strong> on {proposal?.signed_at ? new Date(proposal.signed_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          ) : (
            <div>
              <h3 className="font-medium text-gray-700 mb-4 text-center">Accept This Proposal</h3>
              <div className="max-w-md mx-auto">
                <label className="block text-sm text-gray-600 mb-2">
                  Type your full name to sign
                </label>
                <input
                  type="text"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="signature-input"
                />
                <button
                  onClick={handleSign}
                  disabled={signing || !signatureName.trim()}
                  className="w-full bg-emerald-600 text-white py-4 rounded-lg font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  data-testid="sign-proposal-btn"
                >
                  {signing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5" />
                      Accept & Sign Proposal
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-4 text-center">
                  By signing, you agree to the scope of work and total amount above.
                  Your signature, IP address, and timestamp will be recorded.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Contact Footer */}
        {contractor && (
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Questions? Contact {contractor.company_name}</p>
            <div className="flex items-center justify-center gap-4 mt-2">
              {contractor.phone && (
                <a href={`tel:${contractor.phone}`} className="flex items-center gap-1 text-emerald-600 hover:underline">
                  <Phone className="h-4 w-4" />
                  {contractor.phone}
                </a>
              )}
              {contractor.email && (
                <a href={`mailto:${contractor.email}`} className="flex items-center gap-1 text-emerald-600 hover:underline">
                  <Mail className="h-4 w-4" />
                  {contractor.email}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewProposal;
